//Hooks
import { get } from 'svelte/store';
//Helpers
import {
	get_mode,
	isBluTRV,
	isCamera,
	isGen2EM,
	isProThreeEM,
	isShellyPresence,
	real_id,
} from '../../../interface_helpers';
import {
	isXT1Module,
	isXT1ComponentDevice,
	resolveXT1ComponentChannel,
} from '../../../xt1_helpers/template_helpers';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import {
	getComponentByDeviceMode,
	isCombinationDeviceLightMode,
} from '../../local/core/local_action_components_helpers';
import { getVirtualWebHookList } from '../core/webhook_virtual_helpers';
import {
	getComponentByDevice,
	isVirtualDevice,
} from '../../../virtual_helpers/virtual_components_helpers';
import { getBthomeDeviceModel } from '../../../virtual_helpers/virtual_bthome_helper';
import { deepCopy } from '../../../utilities';
//Config
import {
	bluTRVWebhookListSupported,
	bthomeButtonWebhookListSupported,
	bthomeWebhookListSupported,
	cameraWebhookListSupported,
	lnmWebhookListSupported,
	newBthomeButtonWebhookListSupported,
	virtualWebhookListSupported,
	webhookListActivePowerSupported,
	webhookListPro3EmTotalSupported,
	webhookListSupported,
} from './webhook_list_supported';
import { ACTIVE_POWER_WEBHOOK_SUPPORTED_DEVICES } from 'src/constants';
import { getTRVFirmwareVersion } from '../../../firmware_helpers';
import { compareFWVersions } from '../../../firmware_compare_helpers';
import { getWebHookTypes, isXT1VirtualWebhookComponent } from '../core/webhook_helpers';
//Types
import type { WebHooksParams, WebHookSupportedList } from 'src/types/requests/settings/webhooks';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export const getWebhookListSupported = (
	device: Device,
	peripheralsWebHook = false,
): WebHookSupportedList => {
	const deviceSettings = devices_settings.getByRealId(device.id);
	const lnmTypes = hasLnmComponents(deviceSettings) ? lnmWebhookListSupported.types : {};
	if (peripheralsWebHook) {
		return webhookListSupported;
	}
	if (isXT1VirtualWebhookComponent(device)) {
		const componentType = device.component.split(':')[0];
		if (componentType === 'bthomesensor') {
			return {
				types: {
					...bthomeWebhookListSupported.types,
					...bthomeButtonWebhookListSupported.types,
				},
			};
		}
		if (componentType === 'bthomedevice') {
			return newBthomeButtonWebhookListSupported;
		}
		return virtualWebhookListSupported;
	}
	if (
		(isXT1Module(device.type) && !isXT1ComponentDevice(device)) ||
		/^(virtual-group)$/.test(device.type)
	) {
		return virtualWebhookListSupported;
	}
	if (isCamera(device.type)) {
		return cameraWebhookListSupported;
	}
	if (isBluTRV(device.type) || /^(bthome-device)$/.test(device.type)) {
		const listButtonsSupported =
			getBthomedeviceSupportList(device) || bthomeButtonWebhookListSupported;
		return {
			types: {
				...bthomeWebhookListSupported.types,
				...listButtonsSupported.types,
				...(isBluTRV(device.type) && bluTRVWebhookListSupported.types),
			},
		};
	}
	if (isProThreeEM(device.type)) {
		const deviceMode = get_mode(device, devices_status.getByRealId(device.id), deviceSettings);
		if (deviceMode != 'monophase' && checkNewWebhookSupport(device.id, '1.3.99')) {
			return {
				types: {
					...webhookListSupported.types,
					...webhookListPro3EmTotalSupported.types,
					...lnmTypes,
				},
			};
		}
	} else if (ACTIVE_POWER_WEBHOOK_SUPPORTED_DEVICES.includes(device.type)) {
		return {
			types: {
				...webhookListSupported.types,
				...webhookListActivePowerSupported.types,
				...lnmTypes,
			},
		};
	}
	return {
		types: {
			...webhookListSupported.types,
			...lnmTypes,
		},
	};
};

export const getWebhookList = (
	device: Device,
	params: WebHooksParams[],
	peripheralsWebHook: boolean,
) => {
	let webhooks: WebHooksParams[];
	const deviceSettings = devices_settings.getByRealId(device.id);
	if (
		!peripheralsWebHook &&
		((isXT1Module(device.type) && !isXT1ComponentDevice(device)) ||
			isBluTRV(device.type) ||
			isVirtualDevice(device.type))
	) {
		let vGroupComponents: string[] = getVirtualWebHookList(device);
		webhooks = params.filter((i: { cid: number; event: string }) =>
			vGroupComponents.find((e) => {
				const splitComponent: string[] = e.split(':');
				const type = splitComponent[0];
				const channel = splitComponent[1];
				return Number(channel) == i.cid && i.event.startsWith(`${type}.`);
			}),
		);
	} else {
		const channelDevice =
			isXT1Module(device.type) && isXT1ComponentDevice(device)
				? resolveXT1ComponentChannel(device)
				: device;
		webhooks = params.filter(
			(i: WebHooksParams) =>
				!(peripheralsWebHook && i.event?.startsWith('lnm.')) &&
				filterWebhookList(channelDevice, i, deviceSettings),
		);
	}
	return webhooks;
};

const filterWebhookList = (device: Device, param: WebHooksParams, deviceSetting: any): boolean => {
	if (!device || !param || !deviceSetting) return false;
	if (isXT1Module(device.type) && isXT1ComponentDevice(device)) {
		const [componentType, componentId] = device.component.split(':');
		return param.cid === Number(componentId) && param.event?.startsWith(`${componentType}.`);
	}
	if (
		isCamera(device.type) ||
		isShellyPresence(device.type) ||
		(param.event?.startsWith('lnm.') && hasLnmComponents(deviceSetting)) ||
		(isGen2EM(device.type) && param.event.startsWith('switch.')) || //One output with few channels
		/^(breaker)$/.test(device.category)
	) {
		return true;
	}
	if (param.event.startsWith('input.')) {
		if (device.channels_count === 1 && /^(light|relay|roller)$/.test(device.category)) {
			return true;
		} else if (device.channels_count > 1 && /^(light|relay|roller)$/.test(device.category)) {
			// Show only inputs relevant to the current channel if inputs > channels
			const inputs = Object.keys(deviceSetting).filter((i) => /^input:\d$/.test(i));
			if (device.channels_count === inputs.length / 2) {
				return param.cid >> 1 === device.channel;
			}
		}
	}
	return isSupportedWebHooksByChannel(device, param);
};

const bthomedeviceNewWebhookSupport = (deviceId: string): boolean => {
	return checkNewWebhookSupport(deviceId, '1.5.99');
};

const getBthomedeviceSupportList = (device: Device) => {
	let listButtonsSupported = null;
	if (/^(bthome-device)$/.test(device.type) && bthomedeviceNewWebhookSupport(device.id)) {
		const component = getComponentByDevice(device);
		const deviceStatus = devices_status.getByRealId(device.id);
		const bthomedeviceModel = getBthomeDeviceModel(deviceStatus?.[component]);
		if (bthomedeviceModel) {
			listButtonsSupported = deepCopy(newBthomeButtonWebhookListSupported);
			if (bthomedeviceModel.buttons > 1) {
				Object.values(listButtonsSupported.types).map((i: any) => {
					i.attrs = [
						{
							name: 'idx',
							type: 'button',
							desc: '',
							buttons: bthomedeviceModel.buttons,
						},
						...(i?.attrs || []),
					];
					return i;
				});
			}
		}
	}
	return listButtonsSupported;
};

export const bluTrvNewWebhookSupport = (deviceId: string): boolean => {
	return checkNewWebhookSupport(deviceId, '1.5.0');
};

export const bluTrvNewSchedulePositionSupport = (deviceId: string): boolean => {
	return checkNewWebhookSupport(deviceId, '1.2.6', true);
};

const checkNewWebhookSupport = (
	deviceId: string,
	minVersion: string,
	bluTRVVersion = false,
): boolean => {
	let fwVersion: string = '';
	if (bluTRVVersion) {
		const device = get(devices)[deviceId];
		fwVersion = getTRVFirmwareVersion(device);
	} else {
		const device = get(devices)[real_id(deviceId)];
		const settings = devices_settings.getByRealId(device?.id);
		fwVersion = settings?.DeviceInfo?.ver || settings?.sys?.device?.fw_id;
	}
	return compareFWVersions(fwVersion, '>=', minVersion);
};

const isSupportedWebHooksByChannel = (device: Device, param: WebHooksParams): boolean => {
	if (isCombinationDeviceLightMode(device)) {
		const rgbwwComponent = getComponentByDeviceMode(device);
		if (param.event?.startsWith('input.')) return true;
		return param?.cid === rgbwwComponent?.cid && param.event?.startsWith(`${rgbwwComponent?.mode}.`);
	}
	return param?.cid === device.channel;
};

export const parseWebHooksForAllChannels = (
	device: Device,
	params: WebHooksParams[],
	actions: WebHooksParams[],
) => {
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(device.id);
	if (isXT1Module(device.type) && isXT1ComponentDevice(device)) {
		const xt1Components = (Object.values(get(devices)) as Device[]).filter(
			(item) => real_id(item.id) === real_id(device.id) && isXT1ComponentDevice(item),
		);
		for (const component of xt1Components) {
			if (component.component === device.component) continue;
			addChannelWebhooks(resolveXT1ComponentChannel(component), params, actions, deviceSettings);
		}
		return actions;
	}

	for (let i = 0; i < device?.channels_count; i++) {
		const deviceChannel = get(devices)?.[real_id(device.id) + (i == 0 ? '' : `_${i}`)];
		if (!deviceChannel || i === device.channel) continue;
		addChannelWebhooks(deviceChannel, params, actions, deviceSettings);
	}

	return actions;
};

const addChannelWebhooks = (
	device: Device,
	params: WebHooksParams[],
	actions: WebHooksParams[],
	deviceSettings: DeviceSettingsG2,
) => {
	const webhooks = params.filter((item) => filterWebhookList(device, item, deviceSettings));
	webhooks.forEach((webhook: WebHooksParams) => {
		const action: WebHooksParams = {
			...getWebHookTypes(device),
			...webhook,
			deviceName: device.name,
			source: 'device',
		};
		if (!actions.some((item) => item.id === action.id)) actions.push(action);
	});
};

const hasLnmComponents = (deviceSetting: any): boolean => {
	return Object.keys(deviceSetting || {}).some((key) => /^lnm:\d+$/.test(key));
};
